import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useUser } from '../component/userContext';
import axios from 'axios';
import { API_URL } from '../apiUrl';


const formatPrice = (price) => {
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(price);
};

// Fee calculation per payment method type based on Xendit fee structure
const calculateFee = (method, basePrice) => {
    switch (method) {
        case 'va':
            return 4440; // Rp 4,000 + PPN 11%
        case 'ewallet':
            return Math.ceil(basePrice * 0.015) + 1000; // 1.5% + Rp 1,000
        case 'qris':
            return Math.ceil(basePrice * 0.007); // 0.7%
        default:
            return 0;
    }
};

const CheckoutPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useUser();
    const orderData = location.state;

    const [selectedPayment, setSelectedPayment] = useState('qris');
    const [isProcessing, setIsProcessing] = useState(false);
    const [isLoadingMethods, setIsLoadingMethods] = useState(true);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [errorModal, setErrorModal] = useState({ show: false, title: '', message: '' });

    const paymentMethods = [
        {
            id: 'qris',
            name: 'QRIS',
            desc: 'Scan QR dari semua e-wallet & m-banking',
            icon: 'bi-qr-code',
            xenditCodes: ['QRIS']
        },
        {
            id: 'ewallet',
            name: 'E-Wallet',
            desc: 'GoPay, DANA, OVO, ShopeePay, LinkAja',
            icon: 'bi-wallet2',
            xenditCodes: ['OVO', 'DANA', 'SHOPEEPAY', 'LINKAJA', 'ASTRAPAY', 'NEXCASH', 'JENIUSPAY']
        },
        {
            id: 'va',
            name: 'Virtual Account',
            desc: 'Transfer via BCA, BNI, BRI, Mandiri, dll',
            icon: 'bi-bank',
            xenditCodes: ['BCA', 'BNI', 'BRI', 'MANDIRI', 'PERMATA', 'BSI', 'BJB', 'CIMB', 'BNC', 'SAHABAT_SAMPOERNA']
        }
    ];

    useEffect(() => {
        setIsLoadingMethods(false);
        // Auto-fill WhatsApp from user profile
        if (user?.phoneNumber) {
            setPhoneNumber(user.phoneNumber);
        }
    }, [user]);

    const basePrice = orderData ? orderData.product.price : 0;
    const currentFee = calculateFee(selectedPayment, basePrice);
    const finalPrice = basePrice + currentFee;

    if (!orderData) {
        return (
            <div id="checkoutpage">
                <div className="container checkout-empty">
                    <div className="checkout-empty-card">
                        <i className="bi bi-cart-x checkout-empty-icon"></i>
                        <h3>Tidak Ada Pesanan</h3>
                        <p>Silakan pilih produk terlebih dahulu dari halaman game.</p>
                        <button className="buy-btn" onClick={() => navigate('/')}>
                            <i className="bi bi-house me-2"></i>Kembali ke Home
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const handleConfirm = async () => {
        // Validasi format nomor WhatsApp hanya jika diisi oleh pengguna
        if (phoneNumber.trim()) {
            const waRegex = /^(08|\+62|62)[0-9]{8,13}$/;
            if (!waRegex.test(phoneNumber.trim())) {
                setErrorModal({ show: true, title: 'Format Tidak Valid', message: 'Nomor WhatsApp tidak valid. Format harus diawali 08, 62, atau +62 dengan panjang 10-15 angka.' });
                return;
            }
        }

        setIsProcessing(true);

        try {
            // Get the Xendit payment method codes array
            const selectedMethod = paymentMethods.find(m => m.id === selectedPayment);
            const xenditCodes = selectedMethod ? selectedMethod.xenditCodes : ['QRIS'];

            // First, create the order in our database
            const orderResponse = await axios.post(`${API_URL}/order`, {
                game: orderData.game,
                productName: orderData.product.brand,
                userId: orderData.userId,
                zoneId: orderData.serverId || '',
                price: orderData.product.price,
                productCode: orderData.product.productCode || '',
                email: user?.email || 'guest@familytopup.com',
                phoneNumber: phoneNumber,
                paymentMethod: selectedPayment,
                adminFee: currentFee,
                uniqueCode: 0,
                status: 'Pending'
            });

            const newOrder = orderResponse.data;

            // Then, create the Xendit invoice with restricted payment method
            const invoiceResponse = await axios.post(`${API_URL}/payment/create-invoice`, {
                orderId: newOrder.id,
                grossAmount: finalPrice,
                email: user?.email || 'guest@familytopup.com',
                itemName: `${orderData.game} - ${orderData.product.brand}`,
                paymentMethods: xenditCodes,
                phoneNumber: phoneNumber
            });

            if (invoiceResponse.data && invoiceResponse.data.paymentUrl) {
                window.location.href = invoiceResponse.data.paymentUrl;
            } else {
                throw new Error("Payment URL tidak ditemukan");
            }

        } catch (err) {
            console.error('API error:', err);
            const errMsg = err.response?.data?.error || 'Gagal memproses pembayaran. Silakan coba lagi.';
            // Parse multi-line error message
            const lines = errMsg.split('\n').filter(l => l.trim());
            const title = lines[0] || 'Terjadi Kesalahan';
            const body = lines.slice(1).join('\n') || errMsg;
            setErrorModal({ show: true, title, message: body });
            setIsProcessing(false);
        }
    };



    return (
        <div id="checkoutpage">
            <div className="container my-4">
                <h2 className="checkout-title">
                    <i className="bi bi-bag-check me-2"></i>Checkout
                </h2>

                <div className="row">
                    {/* Order Summary */}
                    <div className="col-lg-5 mb-4">
                        <div className="checkout-card">
                            <h5 className="checkout-card-title">
                                <i className="bi bi-receipt me-2"></i>Ringkasan Pesanan
                            </h5>
                            <div className="checkout-summary-row">
                                <span className="checkout-label">Game</span>
                                <span className="checkout-value">{orderData.game}</span>
                            </div>
                            <div className="checkout-summary-row">
                                <span className="checkout-label">Item</span>
                                <span className="checkout-value">{orderData.product.brand}</span>
                            </div>
                            <div className="checkout-summary-row">
                                <span className="checkout-label">User ID</span>
                                <span className="checkout-value">{orderData.userId}</span>
                            </div>
                            {orderData.serverId && (
                                <div className="checkout-summary-row">
                                    <span className="checkout-label">Server</span>
                                    <span className="checkout-value">{orderData.serverId}</span>
                                </div>
                            )}
                            <div className="checkout-summary-row mt-2">
                                <span className="checkout-label">Harga Produk</span>
                                <span className="checkout-value">{formatPrice(basePrice)}</span>
                            </div>
                            <div className="checkout-summary-row">
                                <span className="text-muted">Biaya Layanan ({paymentMethods.find(m => m.id === selectedPayment)?.name})</span>
                                <span className="text-muted">{formatPrice(currentFee)}</span>
                            </div>
                            <hr />
                            <div className="checkout-summary-row checkout-total">
                                <span>Total Pembayaran</span>
                                <span className="checkout-total-price">{formatPrice(finalPrice)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Payment Methods */}
                    <div className="col-lg-7">
                        <div className="checkout-card">
                            <h5 className="checkout-card-title">
                                <i className="bi bi-credit-card me-2"></i>Metode Pembayaran
                            </h5>
                            <div className="payment-methods">
                                {isLoadingMethods ? (
                                    <div className="text-center py-4">
                                        <div className="spinner-border text-primary" role="status"></div>
                                        <p className="mt-2 text-muted">Memuat metode pembayaran...</p>
                                    </div>
                                ) : (
                                    paymentMethods.map((pm) => (
                                        <div
                                            key={pm.id}
                                            className={`payment-method-card ${selectedPayment === pm.id ? 'payment-selected' : ''}`}
                                            onClick={() => setSelectedPayment(pm.id)}
                                        >
                                            <div className="payment-icon">
                                                <i className={`bi ${pm.icon}`} style={{ fontSize: '24px', color: '#0B8C75' }}></i>
                                            </div>
                                            <div className="payment-info">
                                                <div className="payment-name">{pm.name}</div>
                                                <div className="payment-desc">{pm.desc}</div>
                                                <div className="payment-fee" style={{ fontSize: '0.8rem', color: '#0B8C75', fontWeight: '600', marginTop: '2px' }}>
                                                    Biaya: {formatPrice(calculateFee(pm.id, basePrice))}
                                                </div>
                                            </div>
                                            {selectedPayment === pm.id && (
                                                <div className="payment-check">
                                                    <i className="bi bi-check-circle-fill"></i>
                                                </div>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>

                            {/* WhatsApp Notification Field (Optional for notifications) */}
                            <div className="mt-4 animate__animated animate__fadeIn">
                                <label className="form-label checkout-label" style={{ color: '#0B8C75', fontWeight: 'bold' }}>
                                    <i className="bi bi-whatsapp me-2"></i>Nomor WhatsApp (Opsional)
                                </label>
                                <input
                                    type="tel"
                                    className="form-control game-input"
                                    placeholder="Contoh: 08123456789 (Boleh dikosongkan)"
                                    value={phoneNumber}
                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                    style={{ border: '2px solid #0B8C7533' }}
                                />
                                <small className="text-muted mt-1 d-block">
                                    Dapat diisi agar kami bisa menghubungi Anda jika terjadi kendala transaksi.
                                </small>
                            </div>

                            <button
                                className="buy-btn w-100 mt-4"
                                onClick={handleConfirm}
                                disabled={!selectedPayment || isProcessing}
                            >
                                {isProcessing ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Memproses...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-lock me-2"></i>
                                        Bayar {formatPrice(finalPrice)}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

        {/* Custom Error Modal */}
        {errorModal.show && (
            <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 9999, padding: '20px', animation: 'fadeIn 0.3s ease'
            }} onClick={() => setErrorModal({ show: false, title: '', message: '' })}>
                <div style={{
                    background: 'white', borderRadius: '20px', padding: '0',
                    maxWidth: '420px', width: '100%', boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
                    overflow: 'hidden', animation: 'slideUp 0.3s ease'
                }} onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div style={{
                        background: 'linear-gradient(135deg, #ff6b6b, #ee5a24)',
                        padding: '30px 25px 25px', textAlign: 'center'
                    }}>
                        <div style={{
                            width: '64px', height: '64px', borderRadius: '50%',
                            background: 'rgba(255,255,255,0.2)', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 15px', fontSize: '28px'
                        }}>
                            <i className="bi bi-exclamation-triangle-fill" style={{ color: 'white' }}></i>
                        </div>
                        <h5 style={{ color: 'white', fontWeight: '700', margin: 0, fontSize: '1.15rem' }}>
                            {errorModal.title}
                        </h5>
                    </div>
                    {/* Body */}
                    <div style={{ padding: '25px 25px 15px', textAlign: 'center' }}>
                        <p style={{ color: '#555', lineHeight: '1.7', margin: 0, fontSize: '0.92rem', whiteSpace: 'pre-line' }}>
                            {errorModal.message}
                        </p>
                    </div>
                    {/* Footer */}
                    <div style={{ padding: '10px 25px 25px', textAlign: 'center' }}>
                        <button
                            onClick={() => setErrorModal({ show: false, title: '', message: '' })}
                            style={{
                                background: 'linear-gradient(135deg, #0B8C75, #0fa68e)',
                                color: 'white', border: 'none', borderRadius: '12px',
                                padding: '12px 40px', fontWeight: '600', fontSize: '0.95rem',
                                cursor: 'pointer', width: '100%',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                boxShadow: '0 4px 15px rgba(11,140,117,0.3)'
                            }}
                            onMouseOver={(e) => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 6px 20px rgba(11,140,117,0.4)'; }}
                            onMouseOut={(e) => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 4px 15px rgba(11,140,117,0.3)'; }}
                        >
                            Mengerti
                        </button>
                    </div>
                </div>
            </div>
        )}

        <style>{`
            @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            @keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        `}</style>
    </div>
    );};

export default CheckoutPage;
